import { Popover } from "antd";
import { Badge } from "reactstrap";

// Task-level upload preview columns (task-boq / task-budget / task-resource /
// task-tool), moved verbatim from
// src/pages/pre-built/user-manage/ExcelUploadRequestedList.tsx drawerColConfig
// so the Uploads History drawer and the staged upload modals render the exact
// same preview — same extraction pattern as wbsUploadPreviewColumns.tsx.

const rowStatusColumn: any = {
  title: "Status",
  key: "status",
  width: 140,
  fixed: "left",
  render: (text: any, record: any) => {
    const hasArrayErrors = Array.isArray(record?.Error) && record.Error.length > 0;
    const hasStringError =
      typeof record?.Error === "string" && record.Error.trim().length > 0;
    const hasErrors = hasArrayErrors || hasStringError;

    const errors = hasArrayErrors
      ? record.Error
      : hasStringError
      ? [record.Error]
      : [];

    if (hasErrors) {
      return (
        <Popover
          placement="right"
          content={
            <div style={{ maxWidth: 300 }}>
              {errors.map((error: string, index: number) => (
                <div key={index} style={{ marginBottom: 4 }}>
                  • {error}
                </div>
              ))}
            </div>
          }
          title={
            <span style={{ color: "red", fontWeight: "600" }}>
              Validation Errors
            </span>
          }
        >
          <Badge color="red" className="upload-badge failed">
            Failed
          </Badge>
        </Popover>
      );
    }

    return (
      <Popover
        content={
          <div>
            <p>This record is valid and ready for import</p>
          </div>
        }
        placement="right"
        title={<span style={{ color: "green", fontWeight: "600" }}>Success</span>}
      >
        <Badge color="green" className="upload-badge success">
          Success
        </Badge>
      </Popover>
    );
  },
};

// Overall Uom + Rate, shared by the BOQ / Resource / Tool uploads
const overallRateColumns: any[] = [
  {
    title: "Uom",
    dataIndex: "uomName",
    key: "uomName",
    width: 100,
  },
  {
    title: "Rate",
    dataIndex: "rate",
    key: "rate",
    width: 100,
  },
];

// Quantity column
const quantityColumn: any = {
  title: "Quantity",
  dataIndex: "quantity",
  key: "quantity",
  width: 100,
};

// Rate Analysis columns, shared by the BOQ / Resource / Tool uploads
const rateAnalysisColumns: any[] = [
  {
    title: "Rate Analysis Qty",
    dataIndex: "rateAnalysisQty",
    key: "rateAnalysisQty",
    width: 150,
  },
  {
    title: "Rate Analysis Uom",
    dataIndex: "rateAnalysisUomName",
    key: "rateAnalysisUomName",
    width: 150,
  },
  {
    title: "Rate Analysis Rate",
    dataIndex: "rateAnalysisRate",
    key: "rateAnalysisRate",
    width: 150,
  },
];

// Phase / Activity / Task identity columns, shared by the task-level uploads
const taskIdentityColumns: any[] = [
  {
    title: "Phase Name",
    dataIndex: "phaseName",
    key: "phaseName",
    width: 120,
  },
  {
    title: "Activity Name",
    dataIndex: "activityName",
    key: "activityName",
    width: 120,
  },
  {
    title: "Task Id",
    dataIndex: "taskSequenceId",
    key: "taskSequenceId",
    width: 100,
  },
  {
    title: "Task Name",
    dataIndex: "taskName",
    key: "taskName",
    width: 150,
  },
];

// Banded group headers, matching the Rate Analysis / Overall styling used on the
// task detail & creation screens (blue = Rate Analysis, green = Overall).
const rateAnalysisHeaderStyle: any = {
  background: "#e6f4ff",
  textAlign: "center",
  fontWeight: 600,
};

const overallHeaderStyle: any = {
  background: "#f6ffed",
  textAlign: "center",
  fontWeight: 600,
};

// Grouped header bands (Rate Analysis / Overall) for the Resource / Tool uploads
const rateAnalysisGroup: any = {
  title: "Rate Analysis",
  key: "rateAnalysisGroup",
  align: "center",
  onHeaderCell: () => ({ style: rateAnalysisHeaderStyle }),
  children: rateAnalysisColumns,
};

const overallGroup: any = {
  title: "Overall",
  key: "overallGroup",
  align: "center",
  onHeaderCell: () => ({ style: overallHeaderStyle }),
  children: [quantityColumn, ...overallRateColumns],
};

// ---------------------------------------------------------------- task-boq
export const taskBoqUploadPreviewColumns: any[] = [
  rowStatusColumn,
  ...taskIdentityColumns,
  {
    title: "BOQ Id",
    dataIndex: "boqId",
    key: "boqId",
    width: 120,
  },
  {
    title: "BOQ Name",
    dataIndex: "boqName",
    key: "boqName",
    width: 150,
  },
  {
    title: "BOQ Line Id",
    dataIndex: "boqLineId",
    key: "boqLineId",
    width: 120,
  },
  {
    title: "BOQ Line Name",
    dataIndex: "boqLineName",
    key: "boqLineName",
    width: 150,
  },
  {
    title: "Material Id",
    dataIndex: "materialId",
    key: "materialId",
    width: 100,
  },
  {
    title: "Material Name",
    dataIndex: "materialName",
    key: "materialName",
    width: 150,
  },
  // Norm-based template v2: quantity is computed from the material norm,
  // so the Rate Analysis / Overall bands no longer apply to BOQ uploads
  {
    title: "Output Qty",
    dataIndex: "outputQty",
    key: "outputQty",
    width: 100,
  },
  {
    title: "Output UOM",
    dataIndex: "outputUomName",
    key: "outputUomName",
    width: 120,
  },
  {
    title: "Material Qty",
    dataIndex: "materialQty",
    key: "materialQty",
    width: 110,
  },
  {
    title: "Material UOM",
    dataIndex: "uomName",
    key: "uomName",
    width: 120,
  },
  {
    title: "Material Rate",
    dataIndex: "rate",
    key: "rate",
    width: 110,
  },
  {
    title: "Allocated Qty (computed)",
    dataIndex: "allocateQty",
    key: "allocateQty",
    width: 160,
  },
];

export const TASK_BOQ_UPLOAD_TABLE_WIDTH = 1900;

// ------------------------------------------------------------- task-budget
export const taskBudgetUploadPreviewColumns: any[] = [
  rowStatusColumn,
  ...taskIdentityColumns,
  {
    title: "Budget Id",
    dataIndex: "budgetId",
    key: "budgetId",
    width: 120,
  },
  {
    title: "Budget Name",
    dataIndex: "budgetName",
    key: "budgetName",
    width: 120,
  },
  {
    title: "BudgetLine Id",
    dataIndex: "budgetLineId",
    key: "budgetLineId",
    width: 120,
  },
  {
    title: "BudgetLine Name",
    dataIndex: "budgetLineName",
    key: "budgetLineName",
    width: 150,
  },
  {
    title: "Budget Category",
    dataIndex: "budgetCategory",
    key: "budgetCategory",
    width: 150,
  },
  {
    title: "Allocate Budget",
    dataIndex: "allocateAmount",
    key: "allocatedBudget",
    width: 150,
  },
];

export const TASK_BUDGET_UPLOAD_TABLE_WIDTH = 1000;

// ----------------------------------------------------------- task-resource
export const taskResourceUploadPreviewColumns: any[] = [
  rowStatusColumn,
  ...taskIdentityColumns,
  {
    title: "Labor Type Id",
    dataIndex: "laborTypeId",
    key: "laborTypeId",
    width: 120,
  },
  {
    title: "Labor Type Name",
    dataIndex: "laborTypeName",
    key: "laborTypeName",
    width: 150,
  },
  {
    title: "Labor Category Id",
    dataIndex: "laborCategoryId",
    key: "laborCategoryId",
    width: 150,
  },
  {
    title: "Labor Category Name",
    dataIndex: "laborCategoryName",
    key: "laborCategoryName",
    width: 170,
  },
  {
    title: "Resource Count",
    dataIndex: "resourceCount",
    key: "resourceCount",
    width: 130,
  },
  rateAnalysisGroup,
  overallGroup,
];

export const TASK_RESOURCE_UPLOAD_TABLE_WIDTH = 1900;

// --------------------------------------------------------------- task-tool
export const taskToolUploadPreviewColumns: any[] = [
  rowStatusColumn,
  ...taskIdentityColumns,
  {
    title: "Tool Id",
    dataIndex: "toolId",
    key: "toolId",
    width: 100,
  },
  {
    title: "Tool Name",
    dataIndex: "toolName",
    key: "toolName",
    width: 150,
  },
  {
    title: "Serial Number",
    dataIndex: "serialNumber",
    key: "serialNumber",
    width: 130,
  },
  rateAnalysisGroup,
  overallGroup,
];

export const TASK_TOOL_UPLOAD_TABLE_WIDTH = 1700;
